import React, { useCallback, useEffect, useState } from 'react';
import { Box, Tooltip, Typography, styled } from '@mui/material';

import AIAgentOrb from 'src/components/AIAgentOrb';
import AIManagersService from 'src/services/AIManagersService';
import EventService from 'src/services/EventService';
import Plus from 'src/icons/Plus';
import { ROUTES_URL } from 'src/constants/routes';
import { getAIManagerAvatarPalette } from 'src/utils/aiManagers';
import { history } from 'src/utils/history';

import type { AIManager } from 'src/services/AIManagersService';

const ManagerItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.85),
    minHeight: 86,
    padding: theme.spacing(1),
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.16s ease, opacity 0.16s ease',
    '& + &': {
        marginTop: theme.spacing(0.35),
    },
    '&:hover': {
        backgroundColor: 'var(--lm-surface)',
    },
    '&:hover .manager-actions-line, &:focus-within .manager-actions-line': {
        opacity: 1,
        pointerEvents: 'auto',
    },
}));

const ManagerTopLine = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
}));

const ManagerList = styled(Box)(() => ({
    maxHeight: 300,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    contain: 'layout paint',
    paddingRight: 2,
    '&::-webkit-scrollbar': {
        width: 4,
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'var(--lm-border)',
        borderRadius: 999,
    },
}));

const ManagerInfo = styled(Box)(() => ({
    flex: 1,
    minWidth: 0,
}));

const ManagerName = styled(Typography)(() => ({
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const ManagerMeta = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
}));

const ManagerMetaText = styled(Typography)<{ color?: string }>(({ color }) => ({
    color: color || 'var(--lm-muted)',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0,
    lineHeight: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const ManagerProgressTrack = styled(Box)(() => ({
    backgroundColor: 'var(--lm-border)',
    borderRadius: 999,
    height: 4,
    overflow: 'hidden',
}));

const ManagerProgressFill = styled(Box)<{ color: string; value: number }>(({ color, value }) => ({
    background: color,
    borderRadius: 999,
    height: '100%',
    transition: 'width 0.18s ease',
    width: `${Math.max(0, Math.min(100, value))}%`,
}));

const ManagerActionsLine = styled(Box)(() => ({
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.14s ease',
}));

const ActionButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'subtle',
})<{ subtle?: boolean }>(({ subtle }) => ({
    alignItems: 'center',
    appearance: 'none',
    background: subtle ? 'transparent' : 'var(--lm-surface)',
    border: subtle ? '1px solid transparent' : '1px solid var(--lm-border)',
    borderRadius: 8,
    color: 'var(--lm-muted)',
    cursor: 'pointer',
    display: 'inline-flex',
    height: 28,
    justifyContent: 'center',
    padding: 0,
    transition: 'background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, opacity 0.16s ease',
    width: 28,
    '&:hover': {
        background: 'var(--lm-surface)',
        borderColor: 'var(--lm-border)',
        color: 'var(--lm-text)',
    },
    '& svg': {
        display: 'block',
        height: 15,
        width: 15,
    },
    '& svg path': {
        fill: 'currentColor',
    },
}));

const NewManagerButton = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
        '& .new-manager-text': {
            color: 'var(--lm-accent-ink, var(--lm-accent-ink))',
        },
        '& .plus-icon': {
            '& svg path': {
                fill: 'var(--lm-accent-ink, var(--lm-accent-ink))',
            },
        },
    },
}));

const NewManagerText = styled(Typography)(() => ({
    color: 'var(--lm-muted)',
    fontSize: 14,
    transition: 'color 0.2s ease',
}));

const PlusIcon = styled(Box)(() => ({
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'var(--lm-border)',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    '& svg': {
        width: 16,
        height: 16,
        '& path': {
            fill: 'var(--lm-muted)',
            transition: 'fill 0.2s ease',
        },
    },
}));

type AIManagersMenuProps = {
    managers: AIManager[];
};

function PauseGlyph() {
    return (
        <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M5 3.5C5 3.22386 5.22386 3 5.5 3H6.5C6.77614 3 7 3.22386 7 3.5V12.5C7 12.7761 6.77614 13 6.5 13H5.5C5.22386 13 5 12.7761 5 12.5V3.5Z" />
            <path d="M9 3.5C9 3.22386 9.22386 3 9.5 3H10.5C10.7761 3 11 3.22386 11 3.5V12.5C11 12.7761 10.7761 13 10.5 13H9.5C9.22386 13 9 12.7761 9 12.5V3.5Z" />
        </svg>
    );
}

function PlayGlyph() {
    return (
        <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M5 3.92504C5 3.51977 5.44123 3.26777 5.79099 3.47264L12.7496 7.5476C13.095 7.74992 13.095 8.25008 12.7496 8.4524L5.79099 12.5274C5.44123 12.7322 5 12.4802 5 12.075V3.92504Z" />
        </svg>
    );
}

function SettingsGlyph() {
    return (
        <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M8.75 3.5C8.75 3.08579 8.41421 2.75 8 2.75C7.58579 2.75 7.25 3.08579 7.25 3.5V7.25H3.5C3.08579 7.25 2.75 7.58579 2.75 8C2.75 8.41421 3.08579 8.75 3.5 8.75H7.25V12.5C7.25 12.9142 7.58579 13.25 8 13.25C8.41421 13.25 8.75 12.9142 8.75 12.5V8.75H12.5C12.9142 8.75 13.25 8.41421 13.25 8C13.25 7.58579 12.9142 7.25 12.5 7.25H8.75V3.5Z" />
        </svg>
    );
}

function getStatusMeta(manager: AIManager) {
    if (manager.telegramStatus !== 'connected' && (manager.status === 'running' || manager.status === 'paused')) {
        return { label: 'Telegram не подключён', color: 'var(--lm-danger-ink)', progress: 'var(--lm-danger-ink)' };
    }

    if (manager.status === 'running') {
        return {
            label: 'Продаёт',
            color: 'var(--lm-success-ink)',
            progress: 'var(--lm-accent-fill, var(--lm-accent-ink))',
        };
    }

    if (manager.status === 'paused') {
        return { label: 'Пауза', color: 'var(--lm-muted)', progress: 'var(--lm-muted)' };
    }

    if (manager.status === 'needs_telegram') {
        return { label: 'Telegram не подключён', color: 'var(--lm-danger-ink)', progress: 'var(--lm-danger-ink)' };
    }

    if (manager.status === 'ready_to_launch') {
        return {
            label: 'Готов, Telegram позже',
            color: 'var(--lm-accent-ink, var(--lm-accent-ink))',
            progress: 'var(--lm-accent-fill, var(--lm-accent-ink))',
        };
    }

    if (manager.status === 'error') {
        return { label: 'Ошибка', color: 'var(--lm-danger-ink)', progress: 'var(--lm-danger-ink)' };
    }

    return { label: 'Черновик', color: 'var(--lm-muted)', progress: 'var(--lm-muted)' };
}

function getOrbState(manager: AIManager) {
    if (manager.telegramStatus !== 'connected' && (manager.status === 'running' || manager.status === 'paused')) {
        return 'warning';
    }

    if (manager.status === 'running') return 'writing';
    if (manager.status === 'paused') return 'idle';
    if (manager.status === 'needs_telegram' || manager.status === 'error') return 'warning';
    if (manager.status === 'ready_to_launch') return 'thinking';

    return 'building';
}

const AIManagersMenu: React.FC<AIManagersMenuProps> = ({ managers }) => {
    const [visibleManagers, setVisibleManagers] = useState(managers);
    const [updatingManagerIds, setUpdatingManagerIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        setVisibleManagers(managers);
    }, [managers]);

    const handleNewManagerClick = useCallback(() => {
        history.push(ROUTES_URL.AI_MANAGER_NEW);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleManagerClick = useCallback((manager: AIManager) => {
        history.push(ROUTES_URL.AI_MANAGER_DETAIL.replace(':managerId', manager.id));
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleToggleActive = useCallback(async (event: React.MouseEvent, manager: AIManager) => {
        event.stopPropagation();
        setUpdatingManagerIds((prev) => new Set(prev).add(manager.id));

        try {
            const status = manager.status === 'running' ? 'paused' : 'running';
            const updated = await AIManagersService.updateManagerStatus(manager.id, status);
            setVisibleManagers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            EventService.dispatch('AI_MANAGERS_UPDATED', updated);
        } catch (requestError) {
            const updated = (requestError as { manager?: AIManager }).manager;
            if (updated) {
                setVisibleManagers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
                EventService.dispatch('AI_MANAGERS_UPDATED', updated);
            }
        } finally {
            setUpdatingManagerIds((prev) => {
                const next = new Set(prev);
                next.delete(manager.id);
                return next;
            });
        }
    }, []);

    return (
        <Box
            sx={{
                backgroundColor: 'var(--lm-canvas)',
                borderRadius: 2,
                p: 1,
            }}
        >
            {visibleManagers.length > 0 && (
                <ManagerList>
                    {visibleManagers.map((manager) => {
                        const status = getStatusMeta(manager);
                        const isUpdating = updatingManagerIds.has(manager.id);
                        const canToggle =
                            (manager.leadStreamIds.length > 0 || manager.leadStreams.length > 0) &&
                            manager.telegramStatus === 'connected' &&
                            (manager.status === 'running' ||
                                manager.status === 'paused' ||
                                manager.status === 'ready_to_launch');
                        const toggleLabel = manager.status === 'running' ? 'Пауза' : 'Запустить';
                        const streamText = manager.leadStreamNames.length
                            ? manager.leadStreamNames.join(', ')
                            : 'Потоки не выбраны';

                        return (
                            <ManagerItem
                                key={manager.id}
                                onClick={() => handleManagerClick(manager)}
                            >
                                <ManagerTopLine>
                                    <AIAgentOrb
                                        size={24}
                                        state={getOrbState(manager)}
                                        palette={getAIManagerAvatarPalette(manager.avatarPaletteId)}
                                    />
                                    <ManagerInfo>
                                        <ManagerName
                                            variant="body2"
                                            color="primary"
                                        >
                                            {manager.name}
                                        </ManagerName>
                                    </ManagerInfo>
                                    <ManagerActionsLine className="manager-actions-line">
                                        {canToggle && (
                                            <Tooltip title={toggleLabel}>
                                                <span>
                                                    <ActionButton
                                                        type="button"
                                                        aria-label={toggleLabel}
                                                        title={toggleLabel}
                                                        disabled={isUpdating}
                                                        onClick={(event) => handleToggleActive(event, manager)}
                                                        subtle
                                                    >
                                                        {manager.status === 'running' ? <PauseGlyph /> : <PlayGlyph />}
                                                    </ActionButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Настроить">
                                            <span>
                                                <ActionButton
                                                    type="button"
                                                    aria-label="Настроить"
                                                    title="Настроить"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleManagerClick(manager);
                                                    }}
                                                    subtle
                                                >
                                                    <SettingsGlyph />
                                                </ActionButton>
                                            </span>
                                        </Tooltip>
                                    </ManagerActionsLine>
                                </ManagerTopLine>
                                <ManagerMeta>
                                    <ManagerMetaText color={status.color}>
                                        {status.label}
                                        {manager.dialogBalance > 0 ? ` · ${manager.dialogBalance} диалогов` : ''}
                                        {manager.reviewCount > 0 ? ` · ${manager.reviewCount} проверить` : ''}
                                    </ManagerMetaText>
                                    <ManagerMetaText title={streamText}>{streamText}</ManagerMetaText>
                                    <ManagerProgressTrack>
                                        <ManagerProgressFill
                                            color={status.progress}
                                            value={manager.readiness}
                                        />
                                    </ManagerProgressTrack>
                                </ManagerMeta>
                            </ManagerItem>
                        );
                    })}
                </ManagerList>
            )}
            <NewManagerButton onClick={handleNewManagerClick}>
                <NewManagerText
                    variant="body1"
                    className="new-manager-text"
                >
                    Новый AI-менеджер
                </NewManagerText>
                <PlusIcon className="plus-icon">
                    <Plus />
                </PlusIcon>
            </NewManagerButton>
        </Box>
    );
};

export default React.memo(AIManagersMenu);
